// The cars team's accounts.
//
// They are a separate login, like the walk-in entry team: their own phone and
// password, their own app, and a token that reaches the lot and nothing else.
// They see the deals they work — cars, costs, sales, the owner's terms. What
// they cannot reach is the business above the lot: the CAP↔Cars float and the
// company-wide totals. Those are the superadmin's, and they stay here.
//
// Removing someone is a SOFT delete on purpose: their cars must keep saying
// who added them long after they have gone.

import React, { useCallback, useEffect, useState } from 'react';
import {
  FiCheckCircle,
  FiPhone,
  FiPlus,
  FiRefreshCw,
  FiRotateCcw,
  FiSlash,
  FiTrash2,
  FiUser,
  FiUserPlus,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button, PageHeader } from '../components/common/index.js';
import { Field, Modal, errText, inputCls } from '../components/cars/controls.jsx';
import { carsStaffApi } from '../api/carsApi';

const StaffModal = ({ open, onClose, staff, onSaved }) => {
  const editing = Boolean(staff);
  const [form, setForm] = useState({ name: '', username: '', phone_number: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      name: staff?.name || '',
      username: staff?.username || '',
      phone_number: staff?.phone_number || '',
      email: staff?.email || '',
      password: '',
    });
    setError('');
  }, [open, staff]);

  const submit = async () => {
    setError('');
    if (!form.name.trim()) {
      setError('Give them a name.');
      return;
    }
    // Either identifier will do — a shared desk login usually has just a
    // username, a person usually just a phone.
    if (!form.username.trim() && !form.phone_number.trim()) {
      setError('Give them a username or a phone number — that is what they sign in with.');
      return;
    }
    if (!editing && form.password.trim().length < 4) {
      setError('Give them a password of at least 4 characters.');
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      if (editing) await carsStaffApi.update(staff.id, payload);
      else await carsStaffApi.create(payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(errText(e, 'Could not save.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${staff.name}` : 'Add someone to the cars team'}
      subtitle="They sign in on the Cars tab with a username or a phone number."
    >
      {error && <Alert type="error" message={error} className="mb-4" />}
      <div className="space-y-3">
        <Field label="Name" required>
          <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Vinoth" className={inputCls} />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Username" hint="A shared desk login, e.g. capcars.">
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.replace(/\s/g, '').toLowerCase() }))}
              placeholder="capcars"
              className={inputCls}
            />
          </Field>
          <Field label="Phone" hint="For one person — they can sign in with this instead.">
            <input type="text" value={form.phone_number} onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))} placeholder="9876501234" className={inputCls} />
          </Field>
        </div>
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
          One of the two is enough. Whatever you set here is what they type in the
          &ldquo;Username or phone&rdquo; box on the Cars tab.
        </p>
        <Field label="Email">
          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
        </Field>
        <Field
          label={editing ? 'New password' : 'Password'}
          required={!editing}
          hint={editing ? 'Leave blank to keep the current one.' : undefined}
        >
          <input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>{editing ? 'Save' : 'Add them'}</Button>
      </div>
    </Modal>
  );
};

const CarsStaffBoard = () => {
  const [staff, setStaff] = useState([]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(undefined);   // undefined = closed, null = new

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await carsStaffApi.getList(showRemoved ? { include_deleted: 1 } : {});
      setStaff(data.staff || []);
      setError('');
    } catch (e) {
      setError(errText(e, 'Could not load the cars team.'));
    } finally {
      setLoading(false);
    }
  }, [showRemoved]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, fn) => {
    setBusyId(id);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(errText(e, 'Could not do that.'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PageHeader
        icon={FiUserPlus}
        title="Cars Team"
        meta={`${staff.filter((s) => !s.is_deleted).length} account(s)`}
      >
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
        >
          <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <Button onClick={() => setEditing(null)}>
          <FiPlus className="mr-1.5 h-4 w-4" /> Add someone
        </Button>
      </PageHeader>

      {error && <Alert type="error" message={error} className="mb-4" />}

      <label className="mb-4 inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-slate-500">
        <input
          type="checkbox"
          checked={showRemoved}
          onChange={(e) => setShowRemoved(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-400"
        />
        Show removed accounts
      </label>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading && (
          <div className="space-y-2 p-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />)}
          </div>
        )}

        {!loading && staff.length === 0 && (
          <div className="px-6 py-14 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
              <FiUserPlus className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-bold text-slate-700">Nobody on the cars team yet</p>
            <p className="mx-auto mt-1 max-w-sm text-xs text-slate-400">
              Add someone and they can log in to work the lot — adding cars and recording sales.
            </p>
          </div>
        )}

        {!loading && staff.length > 0 && (
          <ul className="divide-y divide-slate-50">
            {staff.map((s) => (
              <li key={s.id} className={`flex flex-wrap items-center gap-3 px-5 py-4 ${s.is_deleted ? 'opacity-55' : ''}`}>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-bold text-slate-900">
                    {s.name}
                    {s.is_deleted && <span className="rounded-md bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600">Removed</span>}
                    {!s.is_deleted && !s.is_active && <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">Paused</span>}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                    {/* login_id is what they actually type — username if they
                        have one, otherwise the phone. */}
                    <span className="inline-flex items-center gap-1 font-semibold text-slate-500">
                      {s.username ? <FiUser className="h-3 w-3" /> : <FiPhone className="h-3 w-3" />}
                      {s.login_id}
                    </span>
                    {s.username && s.phone_number && <span>{s.phone_number}</span>}
                    {s.email && <span>{s.email}</span>}
                    <span>{s.stats?.cars_added || 0} car(s) added · {s.stats?.cars_sold || 0} sold</span>
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {!s.is_deleted && (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => setEditing(s)}>Edit</Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busyId === s.id}
                        onClick={() => act(s.id, () => carsStaffApi.setActive(s.id, !s.is_active))}
                      >
                        {s.is_active
                          ? <><FiSlash className="mr-1.5 h-3.5 w-3.5" /> Pause</>
                          : <><FiCheckCircle className="mr-1.5 h-3.5 w-3.5" /> Resume</>}
                      </Button>
                      <button
                        type="button"
                        disabled={busyId === s.id}
                        onClick={() => act(s.id, () => carsStaffApi.remove(s.id))}
                        className="rounded-lg p-2 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40"
                        aria-label="Remove"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {s.is_deleted && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busyId === s.id}
                      onClick={() => act(s.id, () => carsStaffApi.restore(s.id))}
                    >
                      <FiRotateCcw className="mr-1.5 h-3.5 w-3.5" /> Bring back
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
        A cars-team login reaches the lot only — the cars, what was spent on them, and the sales
        they record. The money between CAP and the car business, the company-wide totals, and these
        accounts themselves stay on this panel.
      </p>

      <StaffModal
        open={editing !== undefined}
        staff={editing || null}
        onClose={() => setEditing(undefined)}
        onSaved={load}
      />
    </>
  );
};

const CarsStaffPage = () => (
  <MainLayout>
    <CarsStaffBoard />
  </MainLayout>
);

export default CarsStaffPage;
